import React, { useState } from "react";
import { LuPlus, LuShield, LuWallet, LuZap } from "react-icons/lu";
import { MdOutlineClose } from "react-icons/md";
import {
  Modal,
  ModalContent,
  ModalBody,
  Button,
  ModalHeader,
  addToast
} from "@heroui/react";
import { walletCreationApi } from "../../../api/methods-marketplace";
import { useDispatch } from "react-redux";
import { loginSuccess } from "../../../redux/reducer/user_reducer";
import { useWallet } from "../../../context/WalletContext";

const WalletConnectModal = ({
  initialIsWalletOpen,
  initialWalletChange,
  onClose
}) => {
  const [walletCreataionLoading, setWalletCreataionLoading] = useState(false);
  const dispatch = useDispatch();
  const { connectWallet } = useWallet();

  const handleCreateWaallet = async () => {
    setWalletCreataionLoading(true);
    try {
      const response = await walletCreationApi();
      if (response) {
        const data = response?.data;
        console.log("🚀 ~ handleCreateWaal ~ data:", data);
        dispatch(loginSuccess(data));
        onClose();
        // Success toast
        addToast({
          title: "Wallet Created Successfully! 🎉",
          description:
            "Your custodial wallet has been created and is ready to use.",
          color: "success",
          variant: "bordered"
        });
      }
    } catch (error) {
      console.log("🚀 ~ handleCreateWaal ~ error:", error);
      // Different error handling based on error type
      let errorMessage = "An unexpected error occurred. Please try again.";
      let errorTitle = "Wallet Creation Failed";
      if (error?.response) {
        const status = error?.response?.status;
        const data = error?.response?.data?.error;
        switch (status) {
          case 422:
            errorTitle = "Wallet Already Exists";
            errorMessage = data;
            break;
          case 500:
            errorTitle = "Server Error";
            errorMessage =
              "Our servers are experiencing issues. Please try again later.";
            break;
          default:
            errorMessage =
              data || "An unexpected error occurred. Please try again.";
        }
      }
      addToast({
        title: errorTitle,
        description: errorMessage,
        color: "danger",
        variant: "bordered"
      });
    } finally {
      setWalletCreataionLoading(false);
    }
  };

  const handleExternalWalletConnect = () => {
    connectWallet();
    onClose();
  };

  return (
    <>
      <Modal
        isOpen={initialIsWalletOpen}
        onOpenChange={initialWalletChange}
        scrollBehavior="inside"
        classNames={{
          closeButton: "hidden"
        }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="justify-between border-b border-gray-100 px-4">
                <div className="flex flex-col gap-2.5">
                  <h2 className="m-0 text-xl font-semibold text-black">
                    Connect Your Wallet
                  </h2>
                  <p className="text-sm text-zinc-500 font-normal">
                    Choose your preferred connection method
                  </p>
                </div>

                <Button
                  onPress={onClose}
                  className="p-0 w-8 h-8 flex justify-center items-center min-w-8 bg-[transparent]"
                >
                  <MdOutlineClose className="w-6 h-6" />
                </Button>
              </ModalHeader>

              <ModalBody className="p-6 px-4">
                <div className="flex flex-col gap-6">
                  {/* Custodial Wallet Section */}
                  <div className="border border-gray-200 rounded-xl hover:border-gray-300 transition-colors">
                    <div className="p-4 md:p-5">
                      <div className="flex flex-col md:flex-row gap-3 items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 flex justify-center items-center bg-yellow-100 rounded-lg">
                            <LuShield className="w-5 h-5 text-yellow-600" />
                          </div>
                          <div className="flex flex-col gap-1.5 flex-1">
                            <h3 className="m-0 text-base font-semibold text-black">
                              Custodial Wallet
                            </h3>
                            <p className="m-0 text-xs text-gray-500 font-normal">
                              Recommended for beginners
                            </p>
                          </div>
                        </div>
                        <span className="inline-flex items-center rounded-full bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-800 ring-1 ring-yellow-600/20 ring-inset">
                          Easy Setup
                        </span>
                      </div>

                      <p className="text-sm text-neutral-600 mb-4">
                        Create a new wallet that's managed securely for you.
                      </p>

                      <div className="flex flex-wrap items-center text-xs text-gray-500 mb-4 gap-3">
                        <span className="inline-flex items-center rounded-md bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-800 ring-1 ring-yellow-600/20 ring-inset">
                          Secure
                        </span>
                        <span className="inline-flex items-center rounded-md bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-800 ring-1 ring-yellow-600/20 ring-inset">
                          Managed
                        </span>
                        <span className="inline-flex items-center rounded-md bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-800 ring-1 ring-yellow-600/20 ring-inset">
                          Instant
                        </span>
                      </div>
                      <Button
                        onPress={handleCreateWaallet}
                        isLoading={walletCreataionLoading}
                        isDisabled={walletCreataionLoading}
                        className="w-full bg-yellowbuy text-black"
                        radius="md"
                        startContent={<LuPlus className="w-6 h-6" />}
                      >
                        Create Wallet
                      </Button>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <div className="flex-1 border-t border-gray-200"></div>
                    <span className="px-3 text-sm text-gray-500">
                      or connect existing
                    </span>
                    <div className="flex-1 border-t border-gray-200"></div>
                  </div>

                  <div className="border border-gray-200 rounded-xl hover:border-gray-300 transition-colors">
                    <div className="p-4 md:p-5">
                      <div className="flex flex-col md:flex-row gap-3 items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 flex justify-center items-center bg-indigo-100 rounded-lg">
                            <LuWallet className="w-5 h-5 text-indigo-600" />
                          </div>
                          <div className="flex flex-col gap-1.5 flex-1">
                            <h3 className="m-0 text-base font-semibold text-black">
                              External Wallet
                            </h3>
                            <p className="m-0 text-xs text-gray-500 font-normal">
                              Use existing wallet
                            </p>
                          </div>
                        </div>
                        <span className="inline-flex items-center rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 ring-1 ring-indigo-700/10 ring-inset">
                          Self Custody
                        </span>
                      </div>

                      <p className="text-sm text-neutral-600 mb-4">
                        Connect your existing wallet like MetaMask,
                        WalletConnect, Coinbase, and more.
                      </p>

                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-2">
                          <span className="text-lg">🦊</span>
                          <span className="text-lg">🔗</span>
                          <span className="text-lg">🔵</span>
                          <span className="text-lg">🛡️</span>
                          <span className="text-xs text-gray-400 font-medium">
                            more
                          </span>
                        </div>
                        <div className="flex items-center text-xs text-gray-500">
                          <LuZap className="w-3 h-3 mr-1 text-gray-500" />
                          <span>Quick Connect</span>
                        </div>
                      </div>

                      <Button
                        isDisabled={walletCreataionLoading}
                        className="w-full bg-indigo-600 text-white"
                        radius="md"
                        onPress={handleExternalWalletConnect}
                        startContent={<LuWallet className="w-6 h-6" />}
                      >
                        Connect Wallet
                      </Button>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="text-center pt-2">
                    <p className="text-xs text-neutral-500">
                      🔒 All connections are secured with industry-standard
                      encryption
                    </p>
                  </div>
                </div>
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

export default WalletConnectModal;
